//third party imports
import { Component,  OnDestroy, input, signal, WritableSignal, ChangeDetectionStrategy, effect } from '@angular/core';
import { Platform } from '@angular/cdk/platform';

import { Subject } from 'rxjs';

//local imports
import { Transforms} from '@shared/helpers/transforms';
import { UnityConfig } from './unity-configuration';

declare var createUnityInstance: any;

export interface UnityParams {
  key: string,
  value: string
}

@Component({
  selector: 'unity',
  templateUrl: './unity.component.html',
  styleUrls: ['./unity.component.scss'],
  standalone: false,  
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnityComponent implements OnDestroy {

  private _destroying$ = new Subject<void>();
  
  public config = input.required<UnityConfig>();
  public width = input<string>("1500px");
  public height = input<string>("400px");    
  public params = input<UnityParams[]>([]);
  public showFullscreenButton = input(false, {
      transform: Transforms.toBoolean,
  });
  
  public isLoading: WritableSignal<boolean> = signal<boolean>(false);
  public loadingProgress: WritableSignal<number> = signal<number>(0);
  public cacheContext = input<any>(undefined);
  
  private messages: any[] = [];

  constructor(private platform: Platform) {

      effect(() => 
      {
        let context = this.cacheContext();
        
        let cache = this.cacheContext();

        if (!context) return;        
        if (!cache) return;

        this.isLoading.set(true);
        this.loadingProgress.set(0);
    
        let unityLogo: HTMLElement = document.querySelector("#unity-webgl-logo")!;
        unityLogo.style.display = "none";

        let fullscreenButton: HTMLElement = document.querySelector("#unity-fullscreen-button")!;
        fullscreenButton.style.visibility = "hidden";
        fullscreenButton.style.float = "right";
        fullscreenButton.style.position = "relative";
        fullscreenButton.style.zIndex = "100";
            
        setTimeout(() => this.initialize(), 500);


      });
  }

  private initialize() {
    console.debug('UnityComponent: Initializing');

    let canvasPlaceholder: HTMLElement = document.querySelector("#unity-canvas-placeholder")!;
    
    canvasPlaceholder.style.height = "100%";
    canvasPlaceholder.style.width = "100%";

    let unityContext = this.cacheContext();

    if (!unityContext.canvas) {
      // UnityComponent a new canvas instance if it doesn't exist
      unityContext.canvas = document.createElement('canvas');
      unityContext.canvas.setAttribute("id", "unity-canvas");

      const scopeAttr = Array.from(canvasPlaceholder.attributes)
        .map(a => a.name)
        .find(n => n.startsWith("_ngcontent-"));
      
      if (scopeAttr) {
        console.log(`UnityComponent: Adding scope attribute ${scopeAttr} to canvas`);
        unityContext.canvas.setAttribute(scopeAttr, "");
      }

      canvasPlaceholder.appendChild(unityContext.canvas);
    }
    else {
      unityContext.canvas.style.display = "block";
      canvasPlaceholder.appendChild(unityContext.canvas);
    }

    let container: HTMLElement = document.querySelector("#unity-container")!;

    container.style.width = this.width();
    container.style.height = this.height();

    let warningBanner: HTMLElement = document.querySelector("#unity-warning")!;
    
    unityContext.canvas.style.width = "100%"
    unityContext.canvas.style.height = "100%"
    
    if (this.platform.ANDROID || this.platform.IOS) {

      //container.className = "unity-mobile";
      //config.devicePixelRatio = 1;
      warningBanner.style.display = "block";

      setTimeout(() => { warningBanner.style.display = "none"; }, 5000);
    }

    if (unityContext.instance != undefined) {
      
      let params = this.params();

      if (params) {
        params.forEach(param => {
          unityContext.instance.SendMessage("WebDataTransfer", param.key, param.value);
          //console.log("Param sent: " + param.key + " : " + param.value);
        });
      }

      let fullscreenButton: HTMLElement = document.querySelector("#unity-fullscreen-button")!;

      if (this.showFullscreenButton()) {
        fullscreenButton.style.visibility = "visible";
      }

      fullscreenButton.onclick = () => {
        unityContext.instance.SetFullscreen(1);
      };


      this.isLoading.set(false);
      this.loadingProgress.set(100);

    }
    else {

      console.debug("UnityComponent: Creating new instance");
      
      createUnityInstance(unityContext.canvas, this.config(),
        (progress: number) => {
          this.loadingProgress.set(Math.round(100 * progress));
        })
        .then((unityInstance: any) => {
          
          unityContext.instance = unityInstance;
          
          let params = this.params();

          if (params) {
            params.forEach(param => {
              unityContext.instance.SendMessage("WebDataTransfer", param.key, param.value);
              //console.log("Param sent: " + param.key + " : " + param.value);
            });
          }

          let fullscreenButton: HTMLElement = document.querySelector("#unity-fullscreen-button")!;

          if (this.showFullscreenButton()) {
            fullscreenButton.style.visibility = "visible";
          }
          
          fullscreenButton.onclick = () => {
            unityContext.instance.SetFullscreen(1);
          };

          for(var message of this.messages)
          {            
            console.debug("UnityComponent: Broadcasting message", message);
            unityContext.instance.SendMessage("WebDataTransfer", "BroadcastMessage", JSON.stringify(message));
          }
          this.messages = [];

          this.loadingProgress.set(100);
          this.isLoading.set(false);
        })
        .catch((err: any) => {

          console.error("UnityComponent: Error occurred whilst creating unity instance", err);
          
          //TODO:DIW:Add error handling
          //this.messenger.send(error.error, "error");

          this.loadingProgress.set(0);
          this.isLoading.set(false);
        });
    }
  }

  public broadcastMessage(message: any)
  {
    let unityContext = this.cacheContext();
    
    if (unityContext == null || unityContext.instance == null)
    {
      console.debug("UnityComponent: Unity instance not available, queuing message", message);
      this.messages.push(message);
      return;
    }

    console.debug("UnityComponent: Broadcasting message", message);
    unityContext?.instance?.SendMessage("WebDataTransfer", "BroadcastMessage", JSON.stringify(message));
  }


  //@HostListener('window:beforeunload')
  async ngOnDestroy() {
    this._destroying$.next();

    await this.cleanupUnity();

    this._destroying$.complete();
  }

  private async cleanupUnity() {

    let unityContext = this.cacheContext();


    if (!unityContext.instance) return;
    if (!unityContext.canvas) return;

    let fullscreenButton: HTMLElement = document.querySelector("#unity-fullscreen-button")!;
    fullscreenButton.remove();

    let container: HTMLElement = document.querySelector("#unity-canvas-placeholder")!;
    unityContext.canvas.style.display = "none";

    container.removeChild(unityContext.canvas);

    //this.dataContext.canvas.remove();    

    // try {
    //   await unityContext.instance.Quit();
    // }
    // finally {
    //   unityContext.instance = undefined;
    //   document.body.removeChild(unityContext.canvas);

    // }
  }
}


